import { QueryFunctionContext } from '@tanstack/react-query';

import { api } from '../services/api';
import { IDataNoPagination } from '../types/shared';

// ── Tipos (espelham os schemas do backend /api/v1/api-controller) ────────────

// Uma tabela servida por uma rota, com as colunas expostáveis dela.
export interface IApiRecursoTabela {
  tabela_fonte: string;
  slug: string;
  colunas: string[];
}

export interface IApiRecurso {
  id: number;
  slug: string;
  nome: string;
  tabela_fonte: string;
  descricao?: string | null;
  ativo: boolean;
  jwt_ttl_segundos: number;
  colunas_disponiveis: string[];
  // Todas as tabelas da rota (evolução N-tabelas).
  tabelas: IApiRecursoTabela[];
}

// Uma entrada do catálogo de tabelas expostáveis (para o cadastro de rota).
export interface IApiCatalogoTabela {
  tabela: string;
  colunas: string[];
}

export interface IApiToken {
  id: number;
  usuario_id?: number | null;
  api_recurso_id: number;
  external_id: number; // principal/legado (1ª empresa)
  external_ids: number[]; // todas as empresas do token (multi-empresa)
  nome: string;
  status: string;
  usuario_nome?: string | null;
  usuario_email?: string | null;
  api_key_recuperavel: boolean;
  colunas_permitidas: string[];
  colunas_por_tabela: Record<string, string[]>;
}

// Resposta da criação: api_key + hmac_secret em claro (recuperáveis depois).
// base_url = URL pública da API (vinda do backend, fonte única) p/ montar o exemplo.
export interface IApiTokenCriado extends IApiToken {
  api_key: string;
  hmac_secret: string;
  base_url: string;
}

// Resposta do /revelar/: credenciais decifradas p/ reenviar ao cliente.
export interface IApiTokenRevelado {
  api_key?: string | null;
  hmac_secret: string;
  base_url: string;
}

export interface IApiRecursoCreate {
  slug: string;
  nome: string;
  tabela_fonte: string;
  descricao?: string | null;
  jwt_ttl_segundos: number;
  // N tabelas que a rota serve. Vazio → só a tabela_fonte.
  tabelas: { tabela_fonte: string; slug: string }[];
}

export interface IApiTokenCreate {
  usuario_id: number;
  api_recurso_id: number;
  // Empresas que o token lê (multi-empresa: 1 ou mais).
  external_ids: number[];
  nome: string;
  // Allowlist por tabela: { tabela: [colunas ON] }.
  colunas_por_tabela: Record<string, string[]>;
}

export interface IApiRecursoUpdate {
  nome?: string;
  descricao?: string | null;
  ativo?: boolean;
  jwt_ttl_segundos?: number;
}

export interface IApiTokenUpdate {
  nome?: string;
  status?: 'ativo' | 'revogado' | 'lockout';
  colunas_por_tabela?: Record<string, string[]>;
  // Redefine as empresas do token (multi-empresa).
  external_ids?: number[];
}

export interface IApiAccessLog {
  id: number;
  api_cliente_id?: number | null;
  api_token_id?: number | null;
  recurso?: string | null;
  uri?: string | null;
  metodo?: string | null;
  status_http?: number | null;
  external_id?: number | null; // principal/legado (1ª empresa)
  external_ids?: number[] | null; // todas as empresas do token na requisição
  linhas_retornadas?: number | null;
  paginas_retornadas?: number | null;
  ip_hash?: string | null; // IP público do requerente (nome da coluna é legado)
  metadados_request?: Record<string, unknown> | null;
  acessado_em?: string | null;
}

const BASE = '/api/v1/api-controller';

export const useApiController = () => {
  // ── Catálogo (tabelas/colunas expostáveis) ────────────────────────────────
  const getCatalogo = async (): Promise<IDataNoPagination<IApiCatalogoTabela[]>> => {
    const res = await api.get<IDataNoPagination<IApiCatalogoTabela[]>>(`${BASE}/catalogo/`);
    return res.data;
  };

  // ── Recursos (rotas) ──────────────────────────────────────────────────────
  const getRecursos = async (): Promise<IDataNoPagination<IApiRecurso[]>> => {
    const res = await api.get<IDataNoPagination<IApiRecurso[]>>(`${BASE}/recursos/`);
    return res.data;
  };

  const createRecurso = async (
    data: IApiRecursoCreate,
  ): Promise<IDataNoPagination<IApiRecurso>> => {
    const res = await api.post<IDataNoPagination<IApiRecurso>>(`${BASE}/recursos/`, data);
    return res.data;
  };

  // Edita nome/descrição/ativo (suspender) da rota. As TABELAS ficam trancadas.
  const updateRecurso = async (params: {
    id: number;
    data: IApiRecursoUpdate;
  }): Promise<IDataNoPagination<IApiRecurso>> => {
    const res = await api.put<IDataNoPagination<IApiRecurso>>(
      `${BASE}/recursos/${params.id}/`,
      params.data,
    );
    return res.data;
  };

  // Apaga a rota (soft-delete) + revoga todos os tokens dela.
  const deleteRecurso = async (recursoId: number) => {
    const res = await api.delete(`${BASE}/recursos/${recursoId}/`);
    return res.data;
  };

  // ── Tokens ────────────────────────────────────────────────────────────────
  const getTokens = async (key: QueryFunctionContext): Promise<IDataNoPagination<IApiToken[]>> => {
    const filters = (key.queryKey[1] ?? {}) as { usuario_id?: number };
    const res = await api.get<IDataNoPagination<IApiToken[]>>(`${BASE}/tokens/`, {
      params: filters,
    });
    return res.data;
  };

  const createToken = async (
    data: IApiTokenCreate,
  ): Promise<IDataNoPagination<IApiTokenCriado>> => {
    const res = await api.post<IDataNoPagination<IApiTokenCriado>>(`${BASE}/tokens/`, data);
    return res.data;
  };

  const updateToken = async (params: {
    id: number;
    data: IApiTokenUpdate;
  }): Promise<IDataNoPagination<IApiToken>> => {
    const res = await api.put<IDataNoPagination<IApiToken>>(
      `${BASE}/tokens/${params.id}/`,
      params.data,
    );
    return res.data;
  };

  // Exclui (soft-delete) um token — só permitido se já estiver revogado (senão 409).
  const deleteToken = async (tokenId: number) => {
    const res = await api.delete(`${BASE}/tokens/${tokenId}/`);
    return res.data;
  };

  // Faxina: exclui (soft-delete) TODOS os tokens revogados de uma vez.
  const deleteTokensRevogados = async () => {
    const res = await api.delete(`${BASE}/tokens/revogados/`);
    return res.data;
  };

  // Revela api_key + hmac_secret decifrados (p/ reenviar ao cliente).
  const revelarToken = async (tokenId: number): Promise<IDataNoPagination<IApiTokenRevelado>> => {
    const res = await api.get<IDataNoPagination<IApiTokenRevelado>>(
      `${BASE}/tokens/${tokenId}/revelar/`,
    );
    return res.data;
  };

  // ── Auditoria ─────────────────────────────────────────────────────────────
  const getLogs = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IApiAccessLog[]>> => {
    const filters = (key.queryKey[1] ?? {}) as { token_id?: number };
    const res = await api.get<IDataNoPagination<IApiAccessLog[]>>(`${BASE}/logs/`, {
      params: filters,
    });
    return res.data;
  };

  return {
    getCatalogo,
    getRecursos,
    createRecurso,
    updateRecurso,
    deleteRecurso,
    getTokens,
    createToken,
    updateToken,
    deleteToken,
    deleteTokensRevogados,
    revelarToken,
    getLogs,
  };
};
